import React, { useEffect, useMemo, useState } from 'react';

type Props = {
  colors: any;
  buildUrlWithFilters: (base: string) => string;
  buildPostBody?: () => any;
  limit?: number; // default 10
};

type Row = {
  point_code: string;
  unit: string;
  installs: number;
  removals: number;
  total: number;
};

type SortKey = 'total' | 'installs' | 'removals' | 'point_code' | 'unit';

// ───────── App font (force BTitrBold) ─────────
const APP_FONT =
  `"BTitr","B Titr","BTitrBold","IRANSans","Inter",system-ui,"Segoe UI",Roboto,sans-serif`;

const TopIsolationPointsCard: React.FC<Props> = ({
  colors,
  buildUrlWithFilters,
  buildPostBody,
  limit = 10,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // sort state
  const [sortBy, setSortBy] = useState<SortKey>('total');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

  // ---- compact design tokens
  const S = {
    radiusLg: 14,
    radiusSm: 10,
    padCard: 12,
    padRow: 8,
    gap: 6,
    fsXs: 11,
    fsSm: 12,
    fsMd: 13,
    fsNum: 14,
  };

  // ---------- styles
  const card: React.CSSProperties = {
    background: colors.panel,
    border: `1px solid ${colors.border}`,
    borderRadius: S.radiusLg,
    padding: S.padCard,
    boxShadow: '0 3px 10px rgba(0,0,0,.16)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: APP_FONT,
    fontWeight: 700,
  };
  const title: React.CSSProperties = {
    fontWeight: 900,
    marginBottom: 2,
    letterSpacing: 0.2,
    fontSize: S.fsMd,
    fontFamily: APP_FONT,
  };
  const sub: React.CSSProperties = {
    fontSize: S.fsXs,
    color: colors.sub,
    marginBottom: 6,
    fontFamily: APP_FONT,
    fontWeight: 700,
  };

  const headRow: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '44px 140px 1fr 82px 82px 88px',
    gap: S.gap,
    padding: `${S.padRow}px ${S.padRow + 2}px`,
    position: 'sticky',
    top: 0,
    background: colors.panel,
    borderBottom: `1px solid ${colors.border}`,
    zIndex: 1,
    fontSize: S.fsXs,
    color: colors.sub,
    fontWeight: 800,
    fontFamily: APP_FONT, // headers: BtitrBold
  };

  const dataRow: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '44px 140px 1fr 82px 82px 88px',
    gap: S.gap,
    padding: `${S.padRow}px ${S.padRow + 2}px`,
    alignItems: 'center',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: S.fsSm,
    fontFamily: APP_FONT,
    fontWeight: 700,
  };

  const theList: React.CSSProperties = {
    overflowY: 'auto',
    borderRadius: S.radiusSm,
    background: '#0f172a',
    border: `1px solid ${colors.border}`,
  };

  const thBtn = (key: SortKey, label: string) => (
    <button
      onClick={() => {
        if (sortBy === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        else {
          setSortBy(key);
          setSortDir(key === 'point_code' || key === 'unit' ? 'asc' : 'desc');
        }
      }}
      style={{
        textAlign: 'right',
        background: 'transparent',
        border: 'none',
        color: colors.sub,
        cursor: 'pointer',
        fontWeight: 800,
        fontSize: S.fsXs,
        fontFamily: APP_FONT, // ensure BtitrBold on header buttons
      }}
      title="مرتب‌سازی"
    >
      {label} {sortBy === key ? (sortDir === 'desc' ? '▼' : '▲') : ''}
    </button>
  );

  // Persian digits helper
  const num = (v: number) => v.toLocaleString('fa-IR');

  // ---------- data load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr(null);
      const token = localStorage.getItem('token') || '';
      const headersBase: HeadersInit = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const endpoint = '/api/dashboard/top-used';

      try {
        let res: Response;

        if (buildPostBody) {
          const body = buildPostBody();
          res = await fetch(endpoint, {
            method: 'POST',
            headers: { ...headersBase, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
          });
        } else {
          const url = buildUrlWithFilters('/api/isolations/top-operations') + `&limit=${limit}`;
          res = await fetch(url, { headers: headersBase, credentials: 'include' });
        }

        const text = await res.text();
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);
        const j = text ? JSON.parse(text) : {};

        let mapped: Row[] = [];
        if (Array.isArray(j?.top_used_isolation_point)) {
          mapped = (j.top_used_isolation_point as any[]).map((it: any) => {
            const installs = Number(it?.Count ?? 0);
            return {
              point_code: String(it?.Pid ?? it?.Description ?? '-'),
              unit: String(it?.UnitArea ?? '-'),
              installs,
              removals: 0,
              total: installs,
            };
          });
        } else {
          const list = Array.isArray(j) ? j : j?.items ?? j?.data ?? [];
          mapped = list.map((it: any) => ({
            point_code: String(it.point_code ?? it.code ?? '-'),
            unit: String(it.unit ?? it.process_unit ?? '-'),
            installs: Number(it.installs ?? it.install_count ?? 0),
            removals: Number(it.removals ?? it.removal_count ?? 0),
            total:
              Number(it.total) ??
              Number(it.installs ?? 0) + Number(it.removals ?? 0),
          }));
        }

        setRows(mapped.slice(0, limit));
      } catch (e: any) {
        setRows([
          { point_code: '101-101', unit: '1', installs: 2, removals: 0, total: 2 },
          { point_code: '101-201', unit: '1', installs: 1, removals: 0, total: 1 },
          { point_code: '201-101', unit: '1', installs: 1, removals: 0, total: 1 },
          { point_code: '201-201', unit: '1', installs: 1, removals: 0, total: 1 },
          { point_code: '201-301', unit: '1', installs: 1, removals: 0, total: 1 },
        ]);
        setErr(e?.message || 'داده‌های سرور در دسترس نیست (placeholder)');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [buildUrlWithFilters, buildPostBody, limit]);

  // ---------- sort + rank
  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const A = a[sortBy] as any;
      const B = b[sortBy] as any;
      if (typeof A === 'number' && typeof B === 'number') {
        return sortDir === 'desc' ? B - A : A - B;
      }
      return sortDir === 'desc'
        ? String(B).localeCompare(String(A), 'fa')
        : String(A).localeCompare(String(B), 'fa');
    });
    return arr;
  }, [rows, sortBy, sortDir]);

  const maxTotal = Math.max(1, ...sorted.map((r) => r.total));

  // ---------- render
  return (
    <section style={card} dir="rtl">
      <div style={title}>نقاط ایزولاسیون با بیشترین تعداد عملیات</div>
      <div style={sub}>مرتب‌سازی پیش‌فرض: مجموع عملیات از بیشترین به کمترین</div>
      {(loading || err) && (
        <div style={{ ...sub, color: loading ? colors.accent : colors.danger }}>
          {loading ? 'در حال بارگذاری…' : err}
        </div>
      )}

      <div style={theList}>
        {/* header */}
        <div style={headRow}>
          <div># رتبه</div>
          <div>{thBtn('point_code', 'کد/‏PID نقطه')}</div>
          <div>{thBtn('unit', 'واحد فرآیندی')}</div>
          <div style={{ textAlign: 'right' }}>{thBtn('installs', 'نصب/عملیات')}</div>
          <div style={{ textAlign: 'right' }}>{thBtn('removals', 'برداشت')}</div>
          <div style={{ textAlign: 'right' }}>{thBtn('total', 'کل')}</div>
        </div>

        {/* rows */}
        <div>
          {sorted.map((r, i) => (
            <div
              key={r.point_code + i}
              style={{
                ...dataRow,
                background: i % 2 ? 'rgba(255,255,255,0.015)' : 'transparent',
              }}
            >
              {/* rank */}
              <div style={{ fontWeight: 800, fontSize: S.fsNum, fontFamily: APP_FONT }}>
                {num(i + 1)}
              </div>

              {/* code (kept monospace intentionally for readability) */}
              <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 800 }}>
                {r.point_code}
              </div>

              {/* unit */}
              <div style={{ color: colors.sub, fontFamily: APP_FONT, fontWeight: 700 }}>
                {r.unit}
              </div>

              {/* installs */}
              <div style={{ textAlign: 'right', fontSize: S.fsNum, fontFamily: APP_FONT, fontWeight: 900 }}>
                {num(r.installs)}
              </div>

              {/* removals */}
              <div style={{ textAlign: 'right', fontSize: S.fsNum, fontFamily: APP_FONT, fontWeight: 900 }}>
                {num(r.removals)}
              </div>

              {/* total with progress */}
              <div>
                <div style={{ textAlign: 'right', fontWeight: 900, fontSize: S.fsNum, fontFamily: APP_FONT }}>
                  {num(r.total)}
                </div>
                <div
                  style={{
                    height: 4,
                    borderRadius: 6,
                    background: '#0b1220',
                    border: `1px solid ${colors.border}`,
                    overflow: 'hidden',
                    marginTop: 4,
                  }}
                  aria-hidden
                >
                  <div
                    style={{
                      width: `${Math.round((r.total / maxTotal) * 100)}%`,
                      height: '100%',
                      background: colors.accent,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}

          {sorted.length === 0 && (
            <div style={{ padding: 12, color: colors.sub, fontSize: S.fsSm, fontFamily: APP_FONT }}>
              داده‌ای یافت نشد.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TopIsolationPointsCard;
